import { useCallback, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { Column, Row, Spacer } from "../../components/Layout/Layout";
import DeleteTransmitterModal from "../../components/Transmitters/DeleteTransmitterModal";
import {
  DialogActions,
  EditTransmitter,
} from "../../components/Transmitters/EditTransmitter";
import MoveTransmitterModal from "../../components/Transmitters/MoveTransmitterModal";
import { Transmitter } from "../../core/api/transmitters/types";
import useTranslations from "../../core/i18n/useTranslations";
import usePageState from "../../core/pagestate/usePageState";
import useUser from "../../core/user/useUser";
import Button from "../../ui-lib/components/Button/Button";
import Divider from "../../ui-lib/components/Divider/Divider";
import Dropdown from "../../ui-lib/components/Dropdown/Dropdown";
import { IDropdownItem } from "../../ui-lib/components/Dropdown/DropdownItem";
import PageHeader from "../../ui-lib/components/PageHeader/PageHeader";
import TableRowSelectionPopup, {
  TableRowSelectionPopupActions,
} from "../../ui-lib/components/Tables/TableRowSelectionPopup";
import { api as reportApi } from "../../core/api/reports/reports";
import { getErrorKey } from "../../components/Errors/ErrorAlert";
import { notify } from "../../ui-lib/components/Alerts/Toast";
import { RowAction } from "../../ui-lib/components/Tables/Table";
import TransmittersTable from "./TransmittersTable";
import { getTransmittersInStock } from "../../core/api/transmitters/transmitters";
import {
  storeTableState,
  useStoredTableState,
} from "../../core/hooks/filters/useStoredTableState";
import {
  searchStringParser,
  urlSearchParser,
} from "../../ui-lib/utils/urlSearchParser";
import SearchInput from "../../ui-lib/components/Inputs/SearchInput";
import { usePageFilter } from "../../core/hooks/filters/usePageFilter";
import { useQueryState } from "../../core/hooks/filters/useQueryState";
import {
  notifyApiErrors,
  sortParamsToString,
} from "../../core/helpers/helpers";
import { useSortParams } from "../../core/hooks/filters/useSortParams";
import { useOrganisationFilter } from "../../core/hooks/filters/useOrganisationFilter";
import { useCustomersFilter } from "../../core/hooks/filters/useCustomersFilter";
import { useInStockFilter } from "../../core/hooks/filters/useInStockFilter";
import { useTransmitterFilter } from "../../core/hooks/filters/useTransmitterFilter";
import { ClearFilter } from "../../ui-lib/components/Tables/CleatFilters";
import { CustomDropdown } from "../../ui-lib/components/Hierarchy/CustomTreeDropdown/CustomDropdown";
import PrimeModal from "../../ui-lib/components/PrimeModal/PrimeModal";
import {
  breakpoints,
  useWindowDimensions,
} from "../../core/hooks/dimensionProvider";

import styles from "../PagesLayout.module.css";
import filterStyles from "./Transmitters.module.css";
import tableSelectionModule from "../../ui-lib/components/Tables/TableRowSelectionPopup.module.css";
import {
  ICategoryProduct,
  IProductCategory,
} from "../../core/api/products/types";
import { getProducts } from "../../core/api/products/products";
import { TransmitterFilters } from "./TransmitterFilters";
import { getProductName } from "../../ui-lib/utils/getProductName";

export const PAGE_ID = "adminportal/transmitters";

const Transmitters = () => {
  storeTableState(PAGE_ID);
  const pageState = usePageState();
  const user = useUser();
  const t = useTranslations();
  const history = useHistory();
  const tableState = useStoredTableState(PAGE_ID);
  const searchState = urlSearchParser(tableState);
  const { width } = useWindowDimensions();
  const isMobile = width < breakpoints.desktop;

  const clearSelectedFunction = useRef<Function>();
  const editTransmitterRef = useRef<DialogActions>(null);
  const tableRowSelectionPopupRef =
    useRef<TableRowSelectionPopupActions<Record<string, any>>>(null);

  const [selectedItemRows, setSelectedItemRows] = useState<Transmitter[]>([]);
  const [selectedRowAction, setSelectedRowAction] = useState<number>(0);
  const [deletionModalOpen, setDeletionModalOpen] = useState<boolean>(false);
  const [moveModalOpen, setMoveModalOpen] = useState<boolean>(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isExportLoading, setIsExportLoading] = useState(false);
  const [withArgs, setWithArgs] = useState(false);
  const [loading, setLoading] = useState(true);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [transmitters, setTransmitters] = useState<{
    data: Transmitter[];
    amount: number;
  }>();
  const [searchParam, setSearchParam] = useQueryState<{ q: string }>({
    q: searchStringParser(searchState?.q) ?? "",
  });
  const { transmitterFilter, setTransmitterFilter, resetTransmitterFilter } =
    useTransmitterFilter(searchState);
  const { pageFilter, setPageFilter, resetPageFilter } =
    usePageFilter(searchState);
  const { sortParams, setSortParams } = useSortParams(searchState);
  const [categories, setCategories] = useState<IProductCategory[]>([]);
  const [products, setProducts] = useState<ICategoryProduct[]>([]);

  const { customersFilter, setCustomersFilter, resetCustomersFilter } =
    useCustomersFilter(searchState);
  const organisationFilter = useOrganisationFilter(customersFilter);
  const { inStockFilter, setInStockFilter, resetInStockFilter } =
    useInStockFilter(searchState);
  const [stockOptions, setStockOptions] = useState([
    {
      id: 0,
      name: t("Transmitters:in_stock_option"),
      isSelected: inStockFilter.inStock === "1",
    },
    {
      id: 1,
      name: t("Transmitters:connected_option"),
      isSelected: inStockFilter.inStock === "2",
    },
    { id: 2, name: t("Common:all"), isSelected: inStockFilter.inStock === "3" },
  ]);

  const categoryItems = categories.map((c) => ({
    name: t(`Language:${c.name}`),
    id: c.productCategoryId,
    isSelected: c.productCategoryId.toString() === transmitterFilter.categoryId,
  }));

  const productItems = products.map((p) => ({
    name: getProductName(p, t),
    id: p.id,
    isSelected: p.id.toString() === transmitterFilter.productId,
  }));

  const getCategoryProducts = (cats: IProductCategory[], id: number | string) =>
    cats
      .find((c) => c.productCategoryId == +id)
      ?.products?.filter((p) => p.canBeInStock) || [];

  const fetchTransmitters = async (controller?: AbortController) => {
    setLoading(true);
    setTransmitters({ data: [], amount: 0 });
    try {
      const { result, total } = await getTransmittersInStock(
        {
          page: pageFilter.page,
          pageSize: pageFilter.pageSize,
          query: searchParam.q,
          order: sortParamsToString(sortParams),
          organizations: organisationFilter?.value,
          inStock: stockOptions.find((opt) => opt.isSelected)?.id,
          ...transmitterFilter,
        },
        user.authenticatedRequest,
        controller?.signal,
      );
      if (result) {
        setTransmitters({ data: result, amount: total });
      }
    } catch (error: any) {
      notifyApiErrors(error.response?.data?.errors);
    } finally {
      if (!controller?.signal.aborted) {
        setLoading(false);
      }
    }
  };

  const fetchCategoriesWithProducts = async () => {
    setCategoriesLoading(true);
    try {
      const result = await getProducts(user.authenticatedRequest);
      setCategories(result.data);
      if (transmitterFilter.categoryId) {
        setProducts(
          getCategoryProducts(result.data, transmitterFilter.categoryId),
        );
      }
    } catch (error: any) {
      notifyApiErrors(error.response?.data?.errors);
    } finally {
      setCategoriesLoading(false);
    }
  };

  const onDone = () => {
    setIsExportLoading(false);
    setIsExportModalOpen(false);
    clearSelectedFunction.current?.();
    tableRowSelectionPopupRef.current?.close(true);
  };

  const onError = (error: any) => {
    setIsExportLoading(false);

    const errorKey = getErrorKey(error);
    notify({
      message: t(`Errors:${errorKey}`),
      variant: "error",
    });
  };

  const { downloadReport } = reportApi(
    user.authenticatedRequest,
    onDone,
    onError,
  );

  const rowSelectionActionItems: IDropdownItem[] = [
    {
      id: 0,
      name: t("Transmitters:dropdown_move_selected"),
      isSelected: selectedRowAction === 0,
    },
    {
      id: 2,
      name: t("Common:export"),
      isSelected: selectedRowAction === 2,
    },
  ];

  const tryDownloadReport = async () => {
    setIsExportLoading(true);
    await downloadReport({
      reportId: 11,
      reportArguments: {
        runMode: "BySearchCriteria",
        entityIds: withArgs ? selectedItemRows.map((item) => item.id) : [],
        organizationIds:
          customersFilter.customers?.split(",").map((i) => +i) ?? [],
        inStock: +inStockFilter.inStock! - 1,
        searchText: searchParam.q,
        categoryId: transmitterFilter.categoryId
          ? +transmitterFilter.categoryId
          : undefined,
        productId: transmitterFilter.productId
          ? +transmitterFilter.productId
          : undefined,
        model: transmitterFilter.model || undefined,
        manufacturer: transmitterFilter.manufacturer || undefined,
        version: transmitterFilter.version || undefined,
      },
    });
  };

  const resetAllFilters = () => {
    setSearchParam({ q: "" });
    resetTransmitterFilter();
    resetInStockFilter();
    setStockOptions(
      stockOptions.map((opt) => ({
        ...opt,
        isSelected: opt.id === 0,
      })),
    );
    setProducts([]);
    resetCustomersFilter();
  };

  const rowActions = [
    {
      icon: "pencil-alt",
      text: t("Common:label_edit"),
      onClick: ({ id }: { id: any }) =>
        history.push(`/adminportal/transmitters/${id}`),
    } as RowAction<Object>,
  ];

  if (user.config?.show.includes("DeleteTransmitters")) {
    rowSelectionActionItems.push({
      id: 1,
      name: t("Transmitters:dropdown_delete_selected"),
      isSelected: selectedRowAction === 1,
    });

    rowActions.push({
      icon: "trash",
      iconVariant: "secondary",
      text: t("Common:delete"),
      onClick: async (rowData: any) => {
        setSelectedItemRows([rowData as Transmitter]);
        setDeletionModalOpen(true);
      },
    });
  }

  const hasConfigurePermissions = user.config?.show.some((key) =>
    [
      "ConfigureCareAlarms",
      "ConfigureOrdinaryHousingAlarms",
      "ConfigurePropertyAlarms",
      "ConfigureResponseAlarms",
      "ConfigureShelteredHousingAlarms",
      "ConfigurePeripheralAlarms",
    ].includes(key),
  );

  useEffect(() => {
    const controller = new AbortController();
    fetchTransmitters(controller);
    return () => controller.abort();
  }, [
    pageFilter.page,
    pageFilter.pageSize,
    searchParam.q,
    transmitterFilter.categoryId,
    transmitterFilter.productId,
    transmitterFilter.version,
    transmitterFilter.model,
    transmitterFilter.manufacturer,
    sortParams,
    organisationFilter,
    stockOptions,
  ]);

  useEffect(() => {
    fetchCategoriesWithProducts();
  }, []);

  useEffect(() => {
    clearSelectedFunction.current?.();
    tableRowSelectionPopupRef.current?.close(true);
  }, [transmitters]);

  const isFilterApplied =
    !!searchParam.q ||
    !!transmitterFilter.categoryId ||
    !!transmitterFilter.productId ||
    !!transmitterFilter.version ||
    !!transmitterFilter.model ||
    !!transmitterFilter.manufacturer ||
    !!customersFilter.customers ||
    inStockFilter.inStock !== "1";

  const getDeleteModalName = useCallback(() => {
    if (selectedItemRows?.length > 1) {
      return selectedItemRows?.length.toString();
    }
    if (selectedItemRows?.length === 1) {
      return selectedItemRows[0].identifier;
    }
    return "";
  }, [selectedItemRows]);

  return (
    <>
      <PageHeader
        pageTitle
        title={t("Menu:transmitter")}
        icon={pageState.pageIcon}
      >
        {hasConfigurePermissions && (
          <Button
            dataCy="add_transmitter"
            variant="primary"
            text={t("Transmitters:add_new_transmitter")}
            onClick={() => editTransmitterRef.current?.open()}
          />
        )}
        {user.config?.show?.includes("Reports") && (
          <Button
            className="ml-8"
            dataCy="export_transmitters"
            type="button"
            variant="secondary"
            text={t("Common:export")}
            onClick={() => {
              setWithArgs(false);
              setIsExportModalOpen(true);
            }}
            loading={isExportLoading}
            disabled={isExportLoading}
          />
        )}
      </PageHeader>
      <Column type="top" className={styles.pageFilters}>
        <SearchInput
          limit={1}
          placeholder={t("Transmitters:transmitter_search_placeholder")}
          value={searchParam.q}
          onChange={(value: string) => {
            if (searchParam.q !== value) {
              setTransmitters({ data: [], amount: 0 });
              resetPageFilter();
            }
            setSearchParam((prev) => ({ ...prev, q: value }));
          }}
        />
        <Spacer size={16} />
        <Divider />
        <Spacer size={16} />
        <Row type="left" align="start" className={filterStyles.filterContainer}>
          <CustomDropdown
            customerFilter={customersFilter}
            onSelect={(ids: string) => setCustomersFilter({ customers: ids })}
          />
          <Dropdown
            dataCy="transmitter_stock_filter"
            width={200}
            items={stockOptions}
            onSelectItem={(item) => {
              const newStockOptions = stockOptions.map((opt) => ({
                ...opt,
                isSelected: opt.id === item.id,
              }));
              setStockOptions(newStockOptions);
              setInStockFilter({ inStock: `${+item.id + 1}` });
              setPageFilter({ page: 1, pageSize: pageFilter.pageSize });
            }}
            selectedItem={stockOptions.find((opt) => opt.isSelected)}
          />
          {hasConfigurePermissions && (
            <>
              <Dropdown
                withClearItem
                width={205}
                items={categoryItems}
                isLoading={categoriesLoading}
                placeholder={`--${t("Transmitters:create_modal_category")}--`}
                selectedItem={categoryItems.find((r) => r.isSelected)}
                onSelectItem={(i) => {
                  const newCategoryId = i.id === -1 ? "" : i.id.toString();
                  if (newCategoryId === transmitterFilter.categoryId) return;
                  setTransmitterFilter({
                    categoryId: newCategoryId,
                    productId: "",
                  });
                  setProducts(getCategoryProducts(categories, i.id));
                  resetPageFilter();
                }}
                disabled={categoriesLoading}
              />
              <Dropdown
                withFilter
                withClearItem
                width={205}
                maxVisible={isMobile ? 4 : 10}
                items={productItems}
                placeholder={`--${t("Objects:product")}--`}
                selectedItem={productItems.find((r) => r.isSelected)}
                onSelectItem={(i) => {
                  setTransmitterFilter({
                    productId: i.id === -1 ? "" : i.id.toString(),
                  });
                  resetPageFilter();
                }}
                disabled={
                  categoriesLoading ||
                  !transmitterFilter.categoryId ||
                  products.length === 0
                }
              />
            </>
          )}
          <TransmitterFilters
            transmitterFilter={transmitterFilter}
            setTransmitterFilter={setTransmitterFilter}
            resetPageFilter={resetPageFilter}
          />
          {isFilterApplied && (
            <ClearFilter
              text={t("Common:labels_clear_all_filters")}
              onClearClick={resetAllFilters}
              filtersToWatch={[customersFilter, inStockFilter]}
            />
          )}
        </Row>
        <Spacer size={16} />
        <Divider />
        <Spacer size={16} />
      </Column>
      <Column type="top" className={styles.content}>
        <TransmittersTable
          loading={loading}
          transmitters={transmitters?.data ?? []}
          rowActions={rowActions}
          pageSettings={pageFilter}
          totalAmount={transmitters?.amount ?? 0}
          tableRowSelectionPopupRef={tableRowSelectionPopupRef}
          withFilters={isFilterApplied}
          sortParams={sortParams}
          onSortChange={setSortParams}
          resetAllFilters={resetAllFilters}
          onPageSettingsChange={setPageFilter}
          setClearItemsFunction={(func) => {
            clearSelectedFunction.current = func;
          }}
        />
      </Column>

      {/* Modals */}

      <PrimeModal
        withFooter
        withHeader
        dataCy="export_transmitters_modal"
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        header={t("Common:export")}
        fixedWidth="572px"
        loading={isExportLoading}
        submitBtn={{
          text: withArgs
            ? `${t("Common:exportselection")} (${selectedItemRows.length})`
            : t("Common:export"),
          onClick: tryDownloadReport,
        }}
        cancelBtn={{
          text: t("Common:cancel"),
          onClick: () => setIsExportModalOpen(false),
        }}
      >
        {withArgs ? (
          <p>
            {t("Common:export")}{" "}
            <strong>{`${selectedItemRows.length} ${t(
              "Common:transmitters",
            ).toLowerCase()}`}</strong>{" "}
            {t("Common:inlist")} {t("Common:toreport")}
            {"?"}
          </p>
        ) : (
          <p>{t("Transmitters:export_modal_description")}</p>
        )}
      </PrimeModal>
      <EditTransmitter
        ref={editTransmitterRef}
        modalTitle={t("Transmitters:create_transmitter_title")}
        saveButtonTitle={t("Transmitters:create_transmitter")}
        onSubmit={(transmitterId?: string | number) => {
          if (transmitterId) {
            history.push(`/adminportal/transmitters/${transmitterId}`);
          } else {
            fetchTransmitters();
          }
        }}
      />
      <DeleteTransmitterModal
        isOpen={deletionModalOpen}
        onClose={() => setDeletionModalOpen(false)}
        text={
          selectedItemRows?.length > 1
            ? t("Transmitters:delete_multiple_transmitter_confirm")
            : t("Transmitters:delete_transmitter_confirm")
        }
        modalTitle={
          selectedItemRows?.length > 1
            ? t("Transmitters:delete_transmitters")
            : t("Transmitters:delete_transmitter")
        }
        name={getDeleteModalName()}
        onDelete={() => {
          setSelectedItemRows([]);
          setDeletionModalOpen(false);
          clearSelectedFunction.current?.();
          tableRowSelectionPopupRef.current?.close(true);
          setTimeout(() => {
            fetchTransmitters();
          }, 500);
        }}
        transmitterIds={selectedItemRows?.map((s) => s.id.toString()) ?? []}
      />
      <MoveTransmitterModal
        isOpen={moveModalOpen}
        closeModal={setMoveModalOpen}
        onClose={() => {
          clearSelectedFunction.current?.();
          tableRowSelectionPopupRef.current?.close(true);
          setTimeout(() => {
            fetchTransmitters();
          }, 500);
        }}
        text={
          selectedItemRows?.length > 1
            ? t("Transmitters:move_multiple_transmitter_confirm")
            : t("Transmitters:move_transmitter_confirm")
        }
        modalTitle={t("Transmitters:move_transmitters")}
        name={selectedItemRows?.length.toString()}
        transmitters={selectedItemRows}
      />
      <TableRowSelectionPopup
        ref={tableRowSelectionPopupRef}
        renderBody={(selectedItems) => (
          <div className={tableSelectionModule.bodyRow}>
            <Dropdown
              items={rowSelectionActionItems}
              placeholder={t("Transmitters:select_action")}
              selectedItem={rowSelectionActionItems.find((r) => r.isSelected)}
              onSelectItem={(item) => {
                setSelectedRowAction(+item.id);
              }}
              width={!isMobile ? "230px" : undefined}
            />
            {selectedRowAction === 0 && (
              <Button
                variant="primary"
                text={`${t("Transmitters:move_selection")} (${
                  selectedItems.length
                })`}
                onClick={() => {
                  setSelectedItemRows(selectedItems as Transmitter[]);
                  setMoveModalOpen(true);
                }}
              />
            )}
            {selectedRowAction === 1 && (
              <Button
                variant="destructive"
                text={`${t("Transmitters:delete_selection")} (${
                  selectedItems.length
                })`}
                onClick={() => {
                  setSelectedItemRows(selectedItems as Transmitter[]);
                  setDeletionModalOpen(true);
                }}
              />
            )}
            {selectedRowAction === 2 && (
              <Button
                variant="primary"
                text={`${t("Common:exportselection")} (${
                  selectedItems.length
                })`}
                onClick={() => {
                  setSelectedItemRows(selectedItems as Transmitter[]);
                  setWithArgs(true);
                  setIsExportModalOpen(true);
                }}
              />
            )}
            <Button
              variant="secondary"
              text={t("Common:cancel")}
              onClick={() => {
                clearSelectedFunction.current?.();
                tableRowSelectionPopupRef.current?.close(true);
              }}
            />
          </div>
        )}
      />
    </>
  );
};

export default Transmitters;
